import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTradeStats } from "../../../hooks/useTradeStats";
import { trackEvent } from "../../../utils/tracker";
import styled from "@xstyled/styled-components";
import { FiInfo, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useTheme } from "@mui/material";
import { tokens } from "../../../utils/theme";
import { Panel, PanelHeader, PanelTitle } from "../../../components/SharedPageStyles";
import { useJournalData } from "../../../context/JournalContext";
import { API_URL } from "../../../config";


// Local helper to map broker names if needed (or rely on API keys)
const PROPFIRMS_MAPPING = {
    "FTMO": "FTMO",
    "Lucid": "Lucid"
};

const getBrokerRule = (brokerName, assetName, instrumentType = "CFD", customRules = null) => {
    // If no rules loaded yet, return safe default
    if (!customRules) return { contractSize: 100, valuePerPoint: 0.01 };

    // Normalize names
    const brokerKey = PROPFIRMS_MAPPING[brokerName] || brokerName;
    let rules = customRules[brokerKey];

    if (!rules) return { contractSize: 100, valuePerPoint: 0.01 };

    const asset = assetName.toUpperCase();

    // 1. Check specific asset override
    // API returns list of assets? Or dict?
    // Backend `get_all_broker_rules` returns a dict structure matching `INITIAL_DATA`.
    // { "FTMO": { assets: [...], defaults: [...] } }

    // BACKEND returns `assets` as a LIST of objects: [{symbol: "XAUUSD", ...}]
    // Javascript logic expected a DICT: rules.assets[asset].
    // I NEED TO ADAPT THIS LOGIC TO THE BACKEND RESPONSE FORMAT.

    // Backend format:
    // assets: [ {symbol: "XAUUSD", ...}, ... ]
    // defaults: [ {asset_class: "forex", ...}, ... ]

    let assetRule = rules.assets?.find(a => a.symbol === asset);
    if (assetRule) {
        // Backend returns camelCase keys for assets: contractSize, valuePerPoint
        return {
            contractSize: assetRule.contractSize !== undefined ? assetRule.contractSize : assetRule.contract_size,
            valuePerPoint: assetRule.valuePerPoint !== undefined ? assetRule.valuePerPoint : assetRule.value_per_point
        };
    }

    // 2. Check Asset Class Defaults
    // Need to detect asset class locally since we don't have metadata here easily?
    // Or iterate defaults and see if one matches our logic?
    // We still need the logic to determine "what class is this asset?".

    const isJPY = asset.includes("JPY");
    const isCrypto = ["BTC", "ETH", "SOL", "XRP"].some(k => asset.includes(k));
    const isIndices = ["US30", "US100", "US500", "GER30", "NAS100", "US2000", "S&P500", "NASDAQ", "RUSSELL"].some(k => asset.includes(k));
    const isForex = asset.length === 6 && !["XAU", "XAG", "OIL"].some(k => asset.includes(k)) && !isCrypto && !isIndices;

    let targetClass = "commodities"; // Fallback
    if (isJPY) targetClass = "jpy";
    else if (isCrypto) targetClass = "crypto";
    else if (isIndices) targetClass = "indices";
    else if (isForex) targetClass = "forex";

    const defaultRule = rules.defaults?.find(d => d.asset_class === targetClass);
    if (defaultRule) {
        return { contractSize: defaultRule.contract_size, valuePerPoint: defaultRule.value_per_point };
    }

    // Default Fallback
    return { contractSize: 100, valuePerPoint: 0.01 };
};

const CalculatorContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;
  flex: 1;
  width: 100%;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.4)' : 'rgba(15, 118, 110, 0.3)'};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.6)' : 'rgba(15, 118, 110, 0.5)'};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBlock = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BrokerBadge = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  color: ${props => tokens(props.theme.palette.mode).grey[300]};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StyledInput = styled.input`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px rgba(68, 138, 255, 0.1);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    cursor: not-allowed;
    opacity: 0.6;
    border-color: transparent;
  }
  /* Hide native spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const CustomSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CustomSelectButton = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.isOpen ? tokens(props.theme.palette.mode).blueAccent[500] : tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  
  ${props => props.isOpen && `
    box-shadow: 0 0 0 2px rgba(68, 138, 255, 0.1);
  `}
`;

const CustomSelectList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  border-radius: 6px;
  z-index: 50;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.4)' : 'rgba(15, 118, 110, 0.3)'};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.6)' : 'rgba(15, 118, 110, 0.5)'};
  }
`;

const CustomSelectOptGroup = styled.div`
  padding: 8px 10px 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
`;

const CustomSelectOption = styled.div`
  padding: 8px 10px 8px 20px;
  font-size: 14px;
  color: ${props => props.selected ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e') : tokens(props.theme.palette.mode).grey[100]};
  background: ${props => props.selected ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.1)' : 'rgba(15, 118, 110, 0.08)') : 'transparent'};
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const SplitInputWrapper = styled.div`
  display: flex;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.2s;
  align-items: center;

  &:focus-within {
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px rgba(68, 138, 255, 0.1);
  }
`;

const SplitInput = styled.input`
  background: transparent;
  border: none;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  width: 50%;
  text-align: center;

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::placeholder {
    color: ${props => tokens(props.theme.palette.mode).grey[500]};
  }
  /* Hide native spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const SplitDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${props => tokens(props.theme.palette.mode).grey[400]};
  opacity: 0.8;
`;

const ResultCard = styled.div`
  background: ${props => props.highlight ? 'rgba(68, 138, 255, 0.1)' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)')};
  border: 1px solid ${props => props.highlight ? 'rgba(68, 138, 255, 0.3)' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)')};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  text-align: center;
  position: relative; /* For absolute positioning of details */
  min-height: 120px;
  justify-content: center;
`;


const InfoIconWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  color: ${props => tokens(props.theme.palette.mode).grey[300]};
  z-index: 5;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const DetailsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 30, 0.95);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 10;
  cursor: pointer;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ResultLabel = styled.span`
  font-size: 12px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
`;

const ResultValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.color || props.theme.palette.text.primary};
  font-family: 'Roboto Mono', monospace;
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0px;
  opacity: 0;
  transition: opacity 0.2s;
  
  ${InputGroup}:hover & {
    opacity: 1;
  }
  
  ${SplitInputWrapper}:hover & {
    opacity: 1;
  }
`;

const SpinnerBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 10px;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  transition: all 0.1s;
  
  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const NumberInputWrapper = styled.div`
  position: relative;
  width: ${props => props.width || '100%'};
`;

const CustomNumberInput = ({ value, onChange, placeholder, step = 1, variant = 'full', ...props }) => {
    const handleIncrement = (e) => {
        e.preventDefault();
        const current = parseFloat(value) || 0;
        const precision = (step.toString().split('.')[1] || '').length;
        onChange((current + step).toFixed(precision));
    };

    const handleDecrement = (e) => {
        e.preventDefault();
        const current = parseFloat(value) || 0;
        const precision = (step.toString().split('.')[1] || '').length;
        onChange((current - step).toFixed(precision));
    };

    const InputComponent = variant === 'split' ? SplitInput : StyledInput;

    const handleInputChange = (e) => {
        // Only allow numbers and one decimal point
        const val = e.target.value;
        if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
            onChange(val);
        }
    };

    return (
        <NumberInputWrapper width={variant === 'split' ? '50%' : '100%'}>
            <InputComponent
                type="text"
                inputMode="decimal"
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
                style={variant === 'full' ? { paddingRight: '20px' } : { paddingRight: '16px', width: '100%' }}
                {...props}
            />
            <SpinnerWrapper>
                <SpinnerBtn onClick={handleIncrement}>
                    <FiChevronUp size={10} />
                </SpinnerBtn>
                <SpinnerBtn onClick={handleDecrement}>
                    <FiChevronDown size={10} />
                </SpinnerBtn>
            </SpinnerWrapper>
        </NumberInputWrapper>
    );
};

const ASSET_GROUPS = [
    { label: "Commodities", options: ["XAUUSD", "XAGUSD", "USOIL"] },
    { label: "Stock Indices", options: ["US500", "US100", "US2000"] },
    { label: "Currency Indices", options: ["USDX", "EURX", "JPYX"] },
    { label: "Forex", options: ["EURUSD", "USDJPY", "USDCHF", "GBPUSD", "USDCAD", "AUDUSD"] },
    { label: "Crypto", options: ["BTCUSD", "ETHUSD"] }
];

const AssetDropdown = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <CustomSelectWrapper ref={wrapperRef}>
            <CustomSelectButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
                {value}
                <FiChevronDown style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </CustomSelectButton>
            {isOpen && (
                <CustomSelectList>
                    {ASSET_GROUPS.map(group => (
                        <div key={group.label}>
                            <CustomSelectOptGroup>{group.label}</CustomSelectOptGroup>
                            {group.options.map(option => (
                                <CustomSelectOption
                                    key={option}
                                    selected={value === option}
                                    onClick={() => {
                                        onChange(option);
                                        setIsOpen(false);
                                    }}
                                >
                                    {option}
                                </CustomSelectOption>
                            ))}
                        </div>
                    ))}
                </CustomSelectList>
            )}
        </CustomSelectWrapper>
    );
};

const PositionCalculatorTab = () => {
    const theme = useTheme();
    // Inputs
    const [asset, setAsset] = useState("XAUUSD");
    const [entryPrice, setEntryPrice] = useState("");
    const [stopLoss, setStopLoss] = useState("");
    const [takeProfitPrice, setTakeProfitPrice] = useState("");

    const [rrr, setRrr] = useState("2");
    const [rrrRisk, setRrrRisk] = useState("1");
    const [rrrReward, setRrrReward] = useState("2");

    // Context
    const { userProfile, filteredRows, filteredInitialBalance, dateFilter } = useJournalData();
    const stats = useTradeStats(filteredRows, filteredInitialBalance, userProfile, dateFilter);

    // Determine active broker rules source based on unified backend response
    const isPersonal = userProfile.account_type === 'Personal' || userProfile.accountType === 'Personal';

    let activeBroker = "";

    if (isPersonal) {
        activeBroker = userProfile.broker || "";
    } else {
        // For Firm accounts, we MUST prioritize the firm name!
        activeBroker = userProfile.firm || userProfile.broker || "";
    }

    // Fetch Broker Rules from API
    const [fetchedRules, setFetchedRules] = useState(null);

    // Fix: Prioritize profile setting, then check fetchedRules for broker default, thenfallback to CFD
    const activeInstrumentType = useMemo(() => {
        // 1. Check User Profile (Data Driven)
        // Handle both snake_case (Backend) and camelCase (Frontend transform)
        if (userProfile.instrument_type) return userProfile.instrument_type;
        if (userProfile.instrumentType) return userProfile.instrumentType;

        // 2. Fallback: Explicit Override for Lucid (Safety Net)
        // If data is missing/delayed, force Lucid -> Futures
        if (activeBroker === "Lucid" || PROPFIRMS_MAPPING[activeBroker] === "Lucid") {
            return "Futures";
        }

        // 3. Check Broker Rules API
        if (fetchedRules) {
            const key = activeBroker;
            if (fetchedRules[key] && fetchedRules[key].defaultInstrumentType) {
                return fetchedRules[key].defaultInstrumentType;
            }
            // Check mapped
            const mapped = PROPFIRMS_MAPPING[activeBroker];

            if (mapped && fetchedRules[mapped] && fetchedRules[mapped].defaultInstrumentType) {
                return fetchedRules[mapped].defaultInstrumentType;
            }
        }

        return "CFD";
    }, [userProfile, fetchedRules, activeBroker]);

    // UI State
    const [activeDetail, setActiveDetail] = useState(null); // 'lots' | 'units' | null

    // New States
    const [accountBalance, setAccountBalance] = useState("100000");
    const [riskAmount, setRiskAmount] = useState("100");
    const [riskPercent, setRiskPercent] = useState("0.1");
    const [rewardAmount, setRewardAmount] = useState("200");
    const [rewardPercent, setRewardPercent] = useState("0.2");

    // Additional state for displaying safety constraint errors
    const [calcError, setCalcError] = useState("");

    const [contractSize, setContractSize] = useState("100");
    const [valuePerPoint, setValuePerPoint] = useState("1"); // Default 1



    // Sync Account Balance from Profile's current equity
    useEffect(() => {
        if (stats && stats.equity) {
            setAccountBalance(stats.equity.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats.equity]);

    useEffect(() => {
        const fetchRules = async () => {
            try {
                // Use relative path for proxy or full if needed. Assuming proxy set up in package.json or using direct localhost for now if cors allowed.
                // Assuming create-react-app proxy or similar. If not, needs full URL.
                // The API is at localhost:8000. Frontend at 3000.
                const response = await fetch(`${API_URL}/config/broker-rules`);
                if (response.ok) {
                    const data = await response.json();
                    if (data && Object.keys(data).length > 0) {
                        setFetchedRules(data);
                        console.log("Broker Rules Loaded from API:", data);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch broker rules:", error);
            }
        };
        fetchRules();
    }, []);

    // Helper to update RRR state and components
    const updateRrrState = (newRrr) => {
        setRrr(newRrr);
        setRrrRisk("1");
        setRrrReward(newRrr);
    };

    // Auto-detect contract size and value per point based on asset and broker
    useEffect(() => {
        const rule = getBrokerRule(activeBroker, asset, activeInstrumentType, fetchedRules);
        setContractSize(rule.contractSize?.toString() || "100");
        setValuePerPoint(rule.valuePerPoint?.toString() || "0.01");
    }, [asset, activeBroker, activeInstrumentType, fetchedRules]);

    // Calculation API Integration
    const [calculationResult, setCalculationResult] = useState(null);
    const [, setIsCalculating] = useState(false);

    useEffect(() => {
        const calculate = async () => {
            console.log("Re-calculating...", { entryPrice, stopLoss, riskAmount, asset, activeBroker, activeInstrumentType, takeProfitPrice });

            if (!entryPrice || !stopLoss || !riskAmount || !takeProfitPrice) {
                console.log("Missing inputs (including TP), skipping calculation.");
                setCalculationResult(null); // Clear previous results if TP is removed
                return;
            }
            // Basic validation
            if (isNaN(parseFloat(entryPrice)) || isNaN(parseFloat(stopLoss)) || isNaN(parseFloat(riskAmount)) || isNaN(parseFloat(takeProfitPrice))) {
                console.log("Invalid numeric inputs, skipping.");
                return;
            }

            setIsCalculating(true);
            try {
                const payload = {
                    broker: activeBroker,
                    asset: asset,
                    entry: parseFloat(entryPrice),
                    stopLoss: parseFloat(stopLoss),
                    risk: parseFloat(riskAmount),
                    instrumentType: activeInstrumentType,
                    takeProfit: parseFloat(takeProfitPrice) || null
                };

                const response = await fetch(`${API_URL}/calculator/position-size`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (response.ok) {
                    const data = await response.json();
                    setCalculationResult(data);
                    setCalcError("");
                } else {
                    const errData = await response.json();
                    setCalcError(errData.detail || "Calculation Error");
                    setCalculationResult(null);
                }
            } catch (e) {
                console.error("Calculation error:", e);
                setCalcError("Network Error");
                setCalculationResult(null);
            } finally {
                setIsCalculating(false);
            }
        };

        const timer = setTimeout(calculate, 500);
        return () => clearTimeout(timer);
    }, [entryPrice, stopLoss, riskAmount, asset, activeBroker, activeInstrumentType, takeProfitPrice]);

    const getPricePrecision = (priceString) => {
        const parts = priceString.split('.');
        return parts.length > 1 ? parts[1].length : 2;
    };

    const getPrecision = (assetName) => {
        if (assetName.includes('JPY')) return 3;
        if (assetName.includes('XAU') || assetName.includes('XAG')) return 3;
        const isCrypto = ["BTC", "ETH", "SOL", "XRP"].some(k => assetName.includes(k));
        const isIndices = ["US30", "US100", "US500", "GER30", "NAS100", "US2000", "S&P500", "NASDAQ", "RUSSELL", "USDX", "EURX", "JPYX"].some(k => assetName.includes(k));
        if (isCrypto || isIndices) return 2;
        if (assetName.includes('USOIL')) return 3;
        return 5; // Default for forex
    };

    const getStep = (assetName) => {
        return 1 / Math.pow(10, getPrecision(assetName));
    };

    const formatNumber = (num, precision = 2) => {
        if (!num) return "";
        return parseFloat(parseFloat(num).toFixed(precision)).toString();
    };

    // --- Handlers ---

    const handleInputBlur = (setter, value, precision) => {
        setter(formatNumber(value, precision));
    };

    const handleEntryChange = (val) => {
        setEntryPrice(val);
        syncRrrOrTp(val, stopLoss, takeProfitPrice);
    };

    const handleSlChange = (val) => {
        setStopLoss(val);
        syncRrrOrTp(entryPrice, val, takeProfitPrice);
    };

    const syncRrrOrTp = (entryStr, slStr, tpStr) => {
        const entry = parseFloat(entryStr);
        const sl = parseFloat(slStr);
        const tp = parseFloat(tpStr);

        if (!isNaN(entry) && !isNaN(sl) && entry !== sl) {
            // SL + TP are both set → compute RRR
            if (!isNaN(tp) && tpStr.trim() !== "") {
                const riskPerUnit = Math.abs(entry - sl);
                const rewardPerUnit = Math.abs(tp - entry);
                if (riskPerUnit !== 0) {
                    const ratio = rewardPerUnit / riskPerUnit;
                    const newRrr = parseFloat(ratio.toFixed(1)).toString();
                    setRrr(newRrr);
                    setRrrReward(newRrr); // Keep risk side at 1

                    // Sync Euro Rewards
                    const riskAmt = parseFloat(riskAmount);
                    if (!isNaN(riskAmt)) {
                        const newReward = riskAmt * ratio;
                        setRewardAmount(formatNumber(newReward));
                        const bal = parseFloat(accountBalance);
                        if (!isNaN(bal) && bal > 0) {
                            setRewardPercent(formatNumber((newReward / bal) * 100));
                        }
                    }
                }
            }
            // If TP is empty, do NOT auto-fill it — wait for user to provide TP or RR
        }
    };

    const handleBalanceChange = (val) => {
        setAccountBalance(val);
        const bal = parseFloat(val);
        if (!isNaN(bal) && bal > 0) {
            // Update EUR amounts based on existing %
            const rRisk = parseFloat(riskPercent);
            if (!isNaN(rRisk)) {
                setRiskAmount(formatNumber((bal * rRisk) / 100));
            }
            const rReward = parseFloat(rewardPercent);
            if (!isNaN(rReward)) {
                setRewardAmount(formatNumber((bal * rReward) / 100));
            }
        }
    };

    const handleRiskAmountChange = (val) => {
        setRiskAmount(val);
        const risk = parseFloat(val);
        const bal = parseFloat(accountBalance);
        if (!isNaN(risk) && !isNaN(bal) && bal > 0) {
            setRiskPercent(formatNumber((risk / bal) * 100));
        }
        // Sync Reward if RRR exists
        const r = parseFloat(rrr);
        if (!isNaN(risk) && !isNaN(r)) {
            const newReward = formatNumber(risk * r);
            setRewardAmount(newReward);
            if (!isNaN(bal) && bal > 0) {
                setRewardPercent(formatNumber((newReward / bal) * 100));
            }
        }
    };

    const handleRiskPercentChange = (val) => {
        setRiskPercent(val);
        const pct = parseFloat(val);
        const bal = parseFloat(accountBalance);
        if (!isNaN(pct) && !isNaN(bal) && bal > 0) {
            const newRisk = formatNumber((bal * pct) / 100);
            setRiskAmount(newRisk);
            // Sync Reward
            const r = parseFloat(rrr);
            if (!isNaN(r)) {
                const newReward = formatNumber(newRisk * r);
                setRewardAmount(newReward);
                setRewardPercent(formatNumber((newReward / bal) * 100));
            }
        }
    };

    const handleRewardAmountChange = (val) => {
        setRewardAmount(val);
        const reward = parseFloat(val);
        const bal = parseFloat(accountBalance);
        if (!isNaN(reward) && !isNaN(bal) && bal > 0) {
            setRewardPercent(formatNumber((reward / bal) * 100));
        }
        // Update RRR
        const risk = parseFloat(riskAmount);
        if (!isNaN(reward) && !isNaN(risk) && risk !== 0) {
            const newRrr = parseFloat((reward / risk).toFixed(1)).toString();
            updateRrrState(newRrr);
            updateTpFromRrr(parseFloat(newRrr));
        }
    };

    const handleRewardPercentChange = (val) => {
        setRewardPercent(val);
        const pct = parseFloat(val);
        const bal = parseFloat(accountBalance);
        if (!isNaN(pct) && !isNaN(bal) && bal > 0) {
            const newReward = formatNumber((bal * pct) / 100);
            setRewardAmount(newReward);
            // Update RRR
            const risk = parseFloat(riskAmount);
            if (!isNaN(newReward) && !isNaN(risk) && risk !== 0) {
                const newRrr = parseFloat((newReward / risk).toFixed(1)).toString();
                updateRrrState(newRrr);
                updateTpFromRrr(parseFloat(newRrr));
            }
        }
    };

    const handleRrrRiskChange = (val) => {
        setRrrRisk(val);
        const rRisk = parseFloat(val);
        const rReward = parseFloat(rrrReward);
        if (!isNaN(rRisk) && !isNaN(rReward) && rRisk !== 0) {
            const newRrr = parseFloat((rReward / rRisk).toFixed(1)).toString();
            setRrr(newRrr);
            // Update Reward
            const risk = parseFloat(riskAmount);
            if (!isNaN(risk)) {
                const newRewardAmt = formatNumber(risk * newRrr);
                setRewardAmount(newRewardAmt);
                const bal = parseFloat(accountBalance);
                if (!isNaN(bal) && bal > 0) {
                    setRewardPercent(formatNumber((newRewardAmt / bal) * 100));
                }
                // SL + RR → TP  |  TP + RR → SL
                const hasSl = stopLoss && stopLoss.trim() !== "";
                const hasTp = takeProfitPrice && takeProfitPrice.trim() !== "";
                if (hasSl) {
                    updateTpFromRrr(newRrr);
                } else if (hasTp) {
                    updateSlFromRrr(parseFloat(newRrr));
                }
            }
        }
    };

    const handleRrrRewardChange = (val) => {
        setRrrReward(val);
        const rRisk = parseFloat(rrrRisk);
        const rReward = parseFloat(val);
        if (!isNaN(rRisk) && !isNaN(rReward) && rRisk !== 0) {
            const newRrr = parseFloat((rReward / rRisk).toFixed(1)).toString();
            setRrr(newRrr);
            // Update Reward
            const risk = parseFloat(riskAmount);
            if (!isNaN(risk)) {
                const newRewardAmt = formatNumber(risk * newRrr);
                setRewardAmount(newRewardAmt);
                const bal = parseFloat(accountBalance);
                if (!isNaN(bal) && bal > 0) {
                    setRewardPercent(formatNumber((newRewardAmt / bal) * 100));
                }
                // SL + RR → TP  |  TP + RR → SL
                const hasSl = stopLoss && stopLoss.trim() !== "";
                const hasTp = takeProfitPrice && takeProfitPrice.trim() !== "";
                if (hasSl) {
                    updateTpFromRrr(newRrr);
                } else if (hasTp) {
                    updateSlFromRrr(parseFloat(newRrr));
                }
            }
        }
    };

    const updateTpFromRrr = React.useCallback((ratio) => {
        const entry = parseFloat(entryPrice);
        const sl = parseFloat(stopLoss);
        if (!isNaN(entry) && !isNaN(sl) && !isNaN(ratio)) {
            const dist = Math.abs(entry - sl);
            const isLong = entry > sl;
            const tp = isLong ? entry + (dist * ratio) : entry - (dist * ratio);
            const precision = getPricePrecision(entryPrice);
            setTakeProfitPrice(formatNumber(tp, precision));
        }
    }, [entryPrice, stopLoss]);

    // TP + RR → SL: Derive stop loss from take profit and risk:reward ratio
    const updateSlFromRrr = React.useCallback((ratio) => {
        const entry = parseFloat(entryPrice);
        const tp = parseFloat(takeProfitPrice);
        if (!isNaN(entry) && !isNaN(tp) && !isNaN(ratio) && ratio !== 0) {
            const rewardDist = Math.abs(tp - entry);
            const riskDist = rewardDist / ratio;
            const isLong = tp > entry;
            const sl = isLong ? entry - riskDist : entry + riskDist;
            const precision = getPricePrecision(entryPrice);
            setStopLoss(formatNumber(sl, precision));
        }
    }, [entryPrice, takeProfitPrice]);

    const handleTpChange = (val) => {
        setTakeProfitPrice(val);
        const tp = parseFloat(val);
        const entry = parseFloat(entryPrice);
        const sl = parseFloat(stopLoss);
        const risk = parseFloat(riskAmount);
        const bal = parseFloat(accountBalance);

        if (!isNaN(tp) && !isNaN(entry) && !isNaN(sl) && entry !== sl) {
            const riskPerUnit = Math.abs(entry - sl);
            const rewardPerUnit = Math.abs(tp - entry);

            if (riskPerUnit !== 0) {
                const ratio = rewardPerUnit / riskPerUnit;
                const newRrr = parseFloat(ratio.toFixed(1)).toString();
                updateRrrState(newRrr);

                // Update Reward EUR and %
                if (!isNaN(risk)) {
                    const newReward = risk * ratio;
                    setRewardAmount(formatNumber(newReward));
                    if (!isNaN(bal) && bal > 0) {
                        setRewardPercent(formatNumber((newReward / bal) * 100));
                    }
                }
            }
        } else {
            setRrr("");
            setRrrRisk("");
            setRrrReward("");
            setRewardAmount("");
            setRewardPercent("");
        }
    };

    // Initial sync removed to prevent infinite loops and race conditions on first keystrokes.
    // TP/RRR sync is now strictly handled interactively via handleEntryChange, handleSlChange, and handleTpChange

    // --- Calculations ---

    const results = useMemo(() => {
        if (!calculationResult || !entryPrice || !stopLoss) return null;

        const entry = parseFloat(entryPrice);
        const sl = parseFloat(stopLoss);

        const isLong = entry > sl;



        // Simplified Display Objects
        return {
            size: Number(calculationResult.size).toFixed(2),
            units: calculationResult.units,
            lotsFormula: calculationResult.formula,
            direction: isLong ? "LONG" : "SHORT",
            riskDisplay: calculationResult.riskDisplay,
            profitDisplay: calculationResult.profitDisplay,
            riskFormula: calculationResult.riskFormula,
            pointTickInfo: calculationResult.pointTickInfo,
            unitsFormula: calculationResult.unitsFormula
        };
    }, [calculationResult, entryPrice, stopLoss]);

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>Position Calculator</PanelTitle>
            </PanelHeader>
            <CalculatorContainer>
                {/* Inputs Section */}
                <SectionBlock>
                    <SectionTitle>
                        <span>Trade Parameters</span>
                        <BrokerBadge theme={theme}>{activeBroker}</BrokerBadge>
                    </SectionTitle>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <InputGroup>
                            <Label>Account Balance</Label>
                            <CustomNumberInput
                                value={accountBalance}
                                onChange={handleBalanceChange}
                                placeholder="100000"
                                disabled
                            />
                        </InputGroup>

                        <InputGroup>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '-2px' }}>
                                <Label style={{ marginBottom: 0 }}>Asset</Label>
                                <span style={{
                                    fontSize: '10px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                    color: '#888',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    lineHeight: 1
                                }}>
                                    {results?.asset?.includes('Micro') ? 'MICRO FUTURES' : activeInstrumentType.toUpperCase()}
                                </span>
                            </div>
                            <AssetDropdown
                                value={asset}
                                onChange={(val) => {
                                    setAsset(val);
                                    trackEvent('CALCULATOR_ASSET_CHANGE', `Changed asset to ${val}`);
                                }}
                            />
                            <div style={{ display: 'flex', gap: '8px', fontSize: '10px', color: '#888', marginTop: '2px', paddingLeft: '2px' }}>
                                <span>1 Lot = {contractSize}</span>
                                <span>•</span>
                                <div>1 Pt = ${valuePerPoint}</div>
                            </div>
                        </InputGroup>
                    </div>

                    <InputGroup>
                        <Label>Entry Price</Label>
                        <CustomNumberInput
                            value={entryPrice}
                            onChange={handleEntryChange}
                            placeholder="0.00"
                            step={getStep(asset)}
                            onBlur={() => handleInputBlur(setEntryPrice, entryPrice, getPrecision(asset))}
                        />
                    </InputGroup>

                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>STOP LOSS</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <InputGroup>
                            <Label>PRICE</Label>
                            <CustomNumberInput
                                value={stopLoss}
                                onChange={handleSlChange}
                                placeholder="0.00"
                                step={getStep(asset)}
                                onBlur={() => handleInputBlur(setStopLoss, stopLoss, getPrecision(asset))}
                            />
                        </InputGroup>
                        <InputGroup>
                            <Label>RISK ($ / %)</Label>
                            <SplitInputWrapper>
                                <CustomNumberInput
                                    variant="split"
                                    value={riskAmount}
                                    onChange={handleRiskAmountChange}
                                    placeholder="$"
                                    step={10}
                                    onBlur={() => handleInputBlur(setRiskAmount, riskAmount, 2)}
                                />
                                <SplitDivider />
                                <CustomNumberInput
                                    variant="split"
                                    value={riskPercent}
                                    onChange={handleRiskPercentChange}
                                    placeholder="%"
                                    step={0.1}
                                    onBlur={() => handleInputBlur(setRiskPercent, riskPercent, 2)}
                                />
                            </SplitInputWrapper>
                        </InputGroup>
                    </div>

                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginTop: '8px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TAKE PROFIT</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <InputGroup>
                            <Label>PRICE</Label>
                            <CustomNumberInput
                                value={takeProfitPrice}
                                onChange={handleTpChange}
                                placeholder="0.00"
                                step={getStep(asset)}
                                onBlur={() => handleInputBlur(setTakeProfitPrice, takeProfitPrice, getPrecision(asset))}
                            />
                        </InputGroup>
                        <InputGroup>
                            <Label>REWARD ($ / %)</Label>
                            <SplitInputWrapper>
                                <CustomNumberInput
                                    variant="split"
                                    value={rewardAmount}
                                    onChange={handleRewardAmountChange}
                                    placeholder="$"
                                    step={10}
                                    onBlur={() => handleInputBlur(setRewardAmount, rewardAmount, 2)}
                                />
                                <SplitDivider />
                                <CustomNumberInput
                                    variant="split"
                                    value={rewardPercent}
                                    onChange={handleRewardPercentChange}
                                    placeholder="%"
                                    step={0.1}
                                    onBlur={() => handleInputBlur(setRewardPercent, rewardPercent, 2)}
                                />
                            </SplitInputWrapper>
                        </InputGroup>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                        <InputGroup>
                            <Label>R:R</Label>
                            <SplitInputWrapper>
                                <CustomNumberInput
                                    value={rrrRisk}
                                    onChange={handleRrrRiskChange}
                                    placeholder="1"
                                    step={1}
                                    variant="split"
                                />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', color: '#666' }}>:</div>
                                <CustomNumberInput
                                    value={rrrReward}
                                    onChange={handleRrrRewardChange}
                                    placeholder="2"
                                    step={0.1}
                                    variant="split"
                                />
                            </SplitInputWrapper>
                        </InputGroup>
                    </div>


                </SectionBlock>

                {/* Results Section */}
                <SectionBlock>
                    <SectionTitle>Position Sizing</SectionTitle>

                    {calcError && (
                        <div style={{
                            padding: '12px',
                            background: 'rgba(244, 67, 54, 0.15)',
                            borderLeft: '4px solid #f44336',
                            color: '#ff8a80',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '500',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiInfo size={16} />
                            <span>{calcError}</span>
                        </div>
                    )}

                    {results && !calcError ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', justifyContent: 'center' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                marginBottom: '10px'
                            }}>
                                <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '4px',
                                    background: results.direction === 'LONG' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                    color: results.direction === 'LONG' ? '#4caf50' : '#f44336',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    {results.direction}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: '#888' }}>on {asset}</span>
                                    <span style={{
                                        fontSize: '10px',
                                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                        color: '#888',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        {results?.asset?.includes('Micro') ? 'MICRO FUTURES' : activeInstrumentType.toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <ResultCard highlight>
                                    <ResultLabel>{activeInstrumentType === 'Futures' ? 'CONTRACTS' : 'LOTS'}</ResultLabel>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        <ResultValue color="#448aff">{Number(results.size).toLocaleString('en-US', { maximumFractionDigits: 2 })}</ResultValue>
                                    </div>

                                    <InfoIconWrapper onClick={() => setActiveDetail('lots')}>
                                        <FiInfo size={18} />
                                    </InfoIconWrapper>

                                    {activeDetail === 'lots' && (
                                        <DetailsOverlay onClick={() => setActiveDetail(null)}>
                                            <ResultLabel style={{ color: '#4caf50', marginBottom: '8px' }}>{activeInstrumentType === 'Futures' ? 'CONTRACTS' : 'LOTS'} CALCULATION</ResultLabel>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                <ResultLabel style={{ fontSize: '10px', opacity: 0.7, fontStyle: 'italic', textTransform: 'none' }}>
                                                    {activeInstrumentType === 'Futures'
                                                        ? 'Contracts = Risk / (Points × VPP)'
                                                        : 'Lots = Units / ContractSize'}
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '11px', color: '#fff', textTransform: 'none', fontFamily: 'monospace' }}>
                                                    {results.lotsFormula}
                                                </ResultLabel>
                                            </div>
                                            <ResultLabel style={{ fontSize: '9px', marginTop: '12px', opacity: 0.5 }}>Click to close</ResultLabel>
                                        </DetailsOverlay>
                                    )}
                                </ResultCard>
                                <ResultCard highlight>
                                    <ResultLabel>UNITS</ResultLabel>
                                    <ResultValue color="#448aff">{Number(results.units).toLocaleString('en-US', { maximumFractionDigits: 2 })}</ResultValue>

                                    <InfoIconWrapper onClick={() => setActiveDetail('units')}>
                                        <FiInfo size={18} />
                                    </InfoIconWrapper>

                                    {activeDetail === 'units' && (
                                        <DetailsOverlay onClick={() => setActiveDetail(null)}>
                                            <ResultLabel style={{ color: '#448aff', marginBottom: '8px' }}>UNITS CALCULATION</ResultLabel>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                <ResultLabel style={{ fontSize: '10px', opacity: 0.7, fontStyle: 'italic', textTransform: 'none' }}>
                                                    Units = Risk / (Dist × ValuePerPoint)
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '11px', color: '#fff', textTransform: 'none', fontFamily: 'monospace' }}>
                                                    {results.unitsFormula}
                                                </ResultLabel>
                                            </div>
                                            <ResultLabel style={{ fontSize: '9px', marginTop: '12px', opacity: 0.5 }}>Click to close</ResultLabel>
                                        </DetailsOverlay>
                                    )}
                                </ResultCard>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <ResultCard>
                                    <ResultLabel>RISK DIST.</ResultLabel>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        <ResultValue style={{ fontSize: '15px' }}>{results?.riskDisplay?.main || '0 Pts'}</ResultValue>
                                        <div style={{ fontSize: '11px', opacity: 0.6 }}>{results?.riskDisplay?.ticks || '0 Ticks'}</div>
                                        <div style={{
                                            background: 'rgba(244, 67, 54, 0.15)',
                                            color: '#ff8a80',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            marginTop: '4px',
                                            fontWeight: '600',
                                            border: '1px solid rgba(244, 67, 54, 0.3)'
                                        }}>
                                            {results?.riskDisplay?.percent || '0.00%'}
                                        </div>
                                    </div>

                                    <InfoIconWrapper onClick={() => setActiveDetail('risk')}>
                                        <FiInfo size={18} />
                                    </InfoIconWrapper>

                                    {activeDetail === 'risk' && (
                                        <DetailsOverlay onClick={() => setActiveDetail(null)}>
                                            <ResultLabel style={{ color: '#f44336', marginBottom: '8px' }}>RISK CALCULATION</ResultLabel>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                <ResultLabel style={{ fontSize: '10px', opacity: 0.7, fontStyle: 'italic', textTransform: 'none' }}>
                                                    Distance = |Entry - SL|
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '11px', color: '#fff', textTransform: 'none', fontFamily: 'monospace' }}>
                                                    {results.riskFormula}
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '9px', opacity: 0.5, fontStyle: 'italic', marginTop: '4px' }}>
                                                    {results.pointTickInfo}
                                                </ResultLabel>
                                            </div>
                                            <ResultLabel style={{ fontSize: '9px', marginTop: '12px', opacity: 0.5 }}>Click to close</ResultLabel>
                                        </DetailsOverlay>
                                    )}
                                </ResultCard>
                                <ResultCard>
                                    <ResultLabel>PROFIT DIST.</ResultLabel>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        <ResultValue style={{ fontSize: '15px', color: '#4caf50' }}>{results?.profitDisplay?.main || '0 Pts'}</ResultValue>
                                        <div style={{ fontSize: '11px', opacity: 0.6 }}>{results?.profitDisplay?.ticks || '0 Ticks'}</div>
                                        <div style={{
                                            background: 'rgba(76, 175, 80, 0.15)',
                                            color: '#81c784',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            marginTop: '4px',
                                            fontWeight: '600',
                                            border: '1px solid rgba(76, 175, 80, 0.3)'
                                        }}>
                                            {results?.profitDisplay?.percent || '0.00%'}
                                        </div>
                                    </div>

                                    <InfoIconWrapper onClick={() => setActiveDetail('profit')}>
                                        <FiInfo size={18} />
                                    </InfoIconWrapper>

                                    {activeDetail === 'profit' && (
                                        <DetailsOverlay onClick={() => setActiveDetail(null)}>
                                            <ResultLabel style={{ color: '#4caf50', marginBottom: '8px' }}>PROFIT CALCULATION</ResultLabel>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                <ResultLabel style={{ fontSize: '10px', opacity: 0.7, fontStyle: 'italic', textTransform: 'none' }}>
                                                    Distance = |TP - Entry|
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '11px', color: '#fff', textTransform: 'none', fontFamily: 'monospace' }}>
                                                    {results.profitFormula}
                                                </ResultLabel>
                                                <ResultLabel style={{ fontSize: '9px', opacity: 0.5, fontStyle: 'italic', marginTop: '4px' }}>
                                                    {results.pointTickInfo}
                                                </ResultLabel>
                                            </div>
                                            <ResultLabel style={{ fontSize: '9px', marginTop: '12px', opacity: 0.5 }}>Click to close</ResultLabel>
                                        </DetailsOverlay>
                                    )}
                                </ResultCard>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#666',
                            gap: '12px'
                        }}>
                            <div style={{ fontSize: '48px', opacity: 0.2 }}>🧮</div>
                            <div>Enter trade parameters to calculate position size</div>
                        </div>
                    )}
                </SectionBlock>
            </CalculatorContainer>
        </Panel>
    );
};

export default PositionCalculatorTab;
